// /#/reset-password?session=<hex>
// magic link verify (intent=reset) 跳到这里。
// 自动用 query 里的 session 完成登录态 + 强制让用户立刻设新密码。

import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { AuthApiError, setPassword } from "../lib/auth-api";
import { useAuth } from "../lib/use-auth";

type Stage =
  | { kind: "verifying" } // 正在用 session 校验登录态
  | { kind: "ready" }
  | { kind: "saving" }
  | { kind: "done" }
  | { kind: "error"; message: string };

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const { status, session, signInWithSession } = useAuth();
  const nav = useNavigate();
  const querySession = params.get("session");

  const [stage, setStage] = useState<Stage>({ kind: "verifying" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");

  // 第一阶段：用 query 的 session 完成登录态
  useEffect(() => {
    if (!querySession) {
      setStage({ kind: "error", message: "链接缺少 session 参数" });
      return;
    }
    if (status === "authenticated" && session === querySession) {
      setStage({ kind: "ready" });
      return;
    }
    signInWithSession(querySession)
      .then(() => setStage({ kind: "ready" }))
      .catch((e: Error) => setStage({ kind: "error", message: e.message }));
  }, [querySession, status, session, signInWithSession]);

  // 没 session 参数也没登录态 → 跳登录
  if (!querySession && status !== "authenticated") {
    return <Navigate to="/" replace />;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pw !== pw2) {
      setStage({ kind: "error", message: "两次输入不一致" });
      return;
    }
    if (!session) {
      setStage({ kind: "error", message: "会话失效，请重新申请重置邮件" });
      return;
    }
    setStage({ kind: "saving" });
    try {
      await setPassword(session, pw);
      setStage({ kind: "done" });
      setTimeout(() => nav("/rooms", { replace: true }), 1200);
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.serverMessage
          : err instanceof Error
            ? err.message
            : "未知错误";
      setStage({ kind: "error", message });
    }
  }

  if (stage.kind === "verifying") {
    return (
      <section className="page page--login">
        <h1>正在校验重置链接…</h1>
        <p className="muted">请稍候。</p>
      </section>
    );
  }

  if (stage.kind === "done") {
    return (
      <section className="page page--login">
        <h1>密码已重置 ✓</h1>
        <p className="muted">下次可用新密码登录。即将进入 Rooms…</p>
      </section>
    );
  }

  if (stage.kind === "error" && !session) {
    return (
      <section className="page page--login">
        <h1>无法重置</h1>
        <div className="login-hint login-hint--error">
          <p>{stage.message}</p>
        </div>
        <div className="form__actions" style={{ marginTop: 16 }}>
          <button className="btn btn--primary" onClick={() => nav("/forgot-password", { replace: true })}>
            重新申请重置邮件
          </button>
          <button className="btn btn--ghost" onClick={() => nav("/", { replace: true })}>
            返回登录
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="page page--login">
      <h1>设置新密码</h1>
      <p className="muted">新密码至少 8 字符，必须同时包含字母与数字。</p>

      <form className="form" onSubmit={submit}>
        <label className="form__row">
          <span>新密码</span>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            minLength={8}
            autoComplete="new-password"
            required
            autoFocus
            disabled={stage.kind === "saving"}
          />
        </label>
        <label className="form__row">
          <span>再输一次</span>
          <input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={8}
            autoComplete="new-password"
            required
            disabled={stage.kind === "saving"}
          />
        </label>
        <div className="form__actions">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={stage.kind === "saving" || pw.length < 8 || pw !== pw2}
          >
            {stage.kind === "saving" ? "保存中…" : "保存新密码"}
          </button>
        </div>

        {stage.kind === "error" && (
          <div className="login-hint login-hint--error">
            <p>{stage.message}</p>
          </div>
        )}
      </form>
    </section>
  );
}
